import { useState, useCallback, useEffect, useMemo } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Helmet } from "react-helmet-async";
import {
  ArrowLeft, ChevronLeft, ChevronRight, X, Maximize, Bed, Bath, Car,
  Calendar, Flame, DoorOpen, Star, ExternalLink, LandPlot, MapPin, Info,
  ShoppingBag, GraduationCap, Train, Trees, Coffee, Images,
  Hospital, Dumbbell, Clock, Navigation,
} from "lucide-react";
import Layout from "@/components/Layout";
import ContactSection from "@/components/ContactSection";
import PropertyMap from "@/components/PropertyMap";
import { formatPrice } from "@/data/properties";
import { useProperty } from "@/hooks/useProperties";
import { Button } from "@/components/ui/button";
import WhatsAppIcon from "@/components/WhatsAppIcon";
import { optimizedImageUrl } from "@/lib/images";

const descriptionHighlights = /(principios de septiembre de 2026|septiembre de 2026|otoño de 2027|obra nueva|luz natural|jardín privado|piscina|garaje para dos vehículos|265 m²|840 m²)/gi;

type PhotoAmbit = {
  id: string;
  label: string;
  description: string;
  indexes: number[];
};

const ponent35PhotoAmbits: PhotoAmbit[] = [
  { id: "exterior", label: "Exterior y jardín", description: "Fachada, piscina, terraza, jardín y accesos", indexes: [4, 15] },
  { id: "salon", label: "Salón", description: "Zona de estar y vistas interiores principales", indexes: [5, 6, 19, 21] },
  { id: "cocina", label: "Cocina y comedor", description: "Cocina abierta, isla y comedor", indexes: [7, 22, 23, 25] },
  { id: "habitaciones", label: "Habitaciones", description: "Dormitorios y estancias privadas", indexes: [8, 12, 13, 17, 24] },
  { id: "banos", label: "Baños", description: "Baños y acabados de mármol", indexes: [11, 14, 18] },
  { id: "distribucion", label: "Distribución", description: "Pasillos, escaleras, accesos y circulación interior", indexes: [9, 10, 20] },
  { id: "garaje", label: "Garaje", description: "Plaza de aparcamiento y acceso de vehículos", indexes: [26] },
  { id: "planos", label: "Planos", description: "Vistas de planta, renders de distribución y plano de parcela", indexes: [0, 1, 2, 3, 16, 27] },
];

function getPhotoAmbits(images: string[]) {
  const isPonent35Gallery = images.length === 28 && images.some((image) => image.includes("plano-ponent35"));

  if (isPonent35Gallery) {
    return ponent35PhotoAmbits
      .map((ambit) => ({ ...ambit, indexes: ambit.indexes.filter((index) => index < images.length) }))
      .filter((ambit) => ambit.indexes.length > 0);
  }

  return [{ id: "galeria", label: "Galería", description: "Fotos de la propiedad", indexes: images.map((_, index) => index) }];
}

function getAmbitForIndex(index: number, ambits: PhotoAmbit[]) {
  return ambits.find((ambit) => ambit.indexes.includes(index));
}

function EmphasizedDescription({ text }: { text: string }) {
  return (
    <>
      {text.split(descriptionHighlights).map((part, index) =>
        index % 2 === 1 ? <strong key={`${part}-${index}`} className="font-semibold text-foreground">{part}</strong> : part,
      )}
    </>
  );
}

export default function Propiedad() {
  const [params] = useSearchParams();
  const { property, loading } = useProperty(params.get("id"));
  const [currentImg, setCurrentImg] = useState(0);
  const [lightbox, setLightbox] = useState(false);
  const [showAll, setShowAll] = useState(false);
  const [selectedAmbit, setSelectedAmbit] = useState("todo");
  const photoAmbits = useMemo(() => getPhotoAmbits(property?.imagenes ?? []), [property?.imagenes]);
  const fmtEur = (v: number) =>
    new Intl.NumberFormat("es-ES", { style: "currency", currency: "EUR", maximumFractionDigits: 0 }).format(v);

  const lifestyle = useMemo(() => ({
    transport: [
      { icon: Train, name: "Estación R4 Renfe Viladecavalls", time: "5 min", mode: "coche" },
      { icon: Navigation, name: "Acceso C-58 (Barcelona / Manresa)", time: "3 min", mode: "coche" },
      { icon: Navigation, name: "AP-7 dirección Girona/Tarragona", time: "10 min", mode: "coche" },
    ],
    daily: [
      { icon: ShoppingBag, name: "Supermercados y comercios", time: "4 min", mode: "coche" },
      { icon: Coffee, name: "Cafeterías y restaurantes", time: "5 min", mode: "coche" },
      { icon: Hospital, name: "Centro de Atención Primaria", time: "5 min", mode: "coche" },
    ],
    family: [
      { icon: GraduationCap, name: "Colegios e institutos", time: "5 min", mode: "coche" },
      { icon: Dumbbell, name: "Polideportivo municipal", time: "6 min", mode: "coche" },
      { icon: Trees, name: "Parc Natural de Sant Llorenç del Munt", time: "12 min", mode: "coche" },
    ],
  }), []);


  const nextImg = useCallback(() => {
    if (!property) return;
    setCurrentImg((i) => (i + 1) % property.imagenes.length);
  }, [property]);

  const prevImg = useCallback(() => {
    if (!property) return;
    setCurrentImg((i) => (i - 1 + property.imagenes.length) % property.imagenes.length);
  }, [property]);

  useEffect(() => {
    if (!property || property.imagenes.length < 2) return;
    const nextIndex = (currentImg + 1) % property.imagenes.length;
    const preload = new Image();
    preload.src = optimizedImageUrl(property.imagenes[nextIndex], 1280, 82);
  }, [currentImg, property]);

  // Keyboard navigation + scroll lock while the lightbox is open
  useEffect(() => {
    if (!lightbox && !showAll) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") { setLightbox(false); setShowAll(false); }
      if (!lightbox) return;
      if (e.key === "ArrowRight") nextImg();
      if (e.key === "ArrowLeft") prevImg();
    };
    window.addEventListener("keydown", onKey);
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prevOverflow;
    };
  }, [lightbox, showAll, nextImg, prevImg]);

  useEffect(() => {
    if (selectedAmbit === "todo") return;
    if (!photoAmbits.some((ambit) => ambit.id === selectedAmbit)) setSelectedAmbit("todo");
  }, [photoAmbits, selectedAmbit]);

  if (loading) {
    return (
      <Layout>
        <div className="pt-28 pb-20 text-center max-w-7xl mx-auto px-4 min-h-screen">
          <p className="text-muted-foreground">Cargando propiedad...</p>
        </div>
      </Layout>
    );
  }

  if (!property) {
    return (
      <Layout>
        <div className="pt-28 pb-20 text-center max-w-7xl mx-auto px-4">
          <h1 className="text-3xl font-bold mb-4">Propiedad no encontrada</h1>
          <Link to="/Propiedades" className="text-gradient-gold hover:underline">← Volver a propiedades</Link>
        </div>
      </Layout>
    );
  }

  const images = property.imagenes;
  const selectedIndexes = selectedAmbit === "todo"
    ? images.map((_, index) => index)
    : photoAmbits.find((ambit) => ambit.id === selectedAmbit)?.indexes ?? images.map((_, index) => index);
  const selectedPhotos = selectedIndexes.map((index) => ({ index, src: images[index], ambit: getAmbitForIndex(index, photoAmbits) }));
  const estadoLabels: Record<string, string> = {
    nuevo: "Obra nueva", excelente: "Excelente", bueno: "Bueno", a_reformar: "A reformar",
  };

  const specs = [
    { icon: Maximize, label: "Superficie", value: `${property.metros_cuadrados} m²` },
    property.metros_parcela && { icon: LandPlot, label: "Parcela", value: `${property.metros_parcela} m²` },
    { icon: Bed, label: "Habitaciones", value: property.habitaciones },
    { icon: Bath, label: "Baños", value: property.banos },
    property.garaje && { icon: Car, label: "Garaje", value: `${property.plazas_garaje || 1} plaza${(property.plazas_garaje || 1) > 1 ? "s" : ""}` },
  ].filter(Boolean) as { icon: any; label: string; value: string | number }[];

  const infoItems = [
    property.ano_construccion && { icon: Calendar, label: "Año de construcción", value: property.ano_construccion },
    { icon: Star, label: "Estado de conservación", value: estadoLabels[property.estado_conservacion] || property.estado_conservacion },
    property.carpinteria && { icon: DoorOpen, label: "Carpintería", value: property.carpinteria },
  ].filter(Boolean) as { icon: any; label: string; value: string | number }[];

  return (
    <Layout>
      <Helmet>
        <title>{`${property.titulo} | Sercom`.slice(0, 60)}</title>
        <meta name="description" content={`${property.titulo} en ${property.ubicacion}. ${property.metros_cuadrados} m², ${property.habitaciones} habitaciones, ${property.banos} baños. ${formatPrice(property.precio)}.`.slice(0, 160)} />
        <link rel="canonical" href={`https://sercomcs.com/Propiedad?id=${property.id}`} />
        <meta property="og:type" content="website" />
        <meta property="og:title" content={`${property.titulo} | Sercom`} />
        <meta property="og:description" content={`${property.metros_cuadrados} m² · ${property.habitaciones} hab · ${formatPrice(property.precio)} — ${property.ubicacion}`} />
        <meta property="og:url" content={`https://sercomcs.com/Propiedad?id=${property.id}`} />
        {property.imagen_principal && <meta property="og:image" content={property.imagen_principal} />}
        <script type="application/ld+json">{JSON.stringify({
          "@context": "https://schema.org",
          "@type": "Product",
          name: property.titulo,
          description: property.descripcion,
          image: property.imagenes?.length ? property.imagenes : [property.imagen_principal],
          offers: {
            "@type": "Offer",
            price: property.precio,
            priceCurrency: "EUR",
            availability: property.estado === "disponible" ? "https://schema.org/InStock" : "https://schema.org/OutOfStock",
            url: `https://sercomcs.com/Propiedad?id=${property.id}`,
          },
        })}</script>
      </Helmet>
      <div className="property-editorial bg-background text-foreground">
        {/* Top bar with breadcrumb */}
        <div className="pt-20 md:pt-24 pb-3 border-b">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between gap-4">
            <Link
              to="/Propiedades"
              className="inline-flex items-center gap-2 text-xs tracking-[0.2em] uppercase text-muted-foreground hover:text-foreground transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Volver a propiedades
            </Link>
            <Button asChild size="sm" className="rounded-full shadow-sm">
              <a href="#contacto">Más información</a>
            </Button>
          </div>
        </div>

        {/* Property overview */}
        <section className="mx-auto max-w-7xl px-4 py-3 sm:px-6 md:py-4 lg:px-8">
          <motion.header
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.65, ease: "easeOut" }}
            className="mb-4 flex flex-col justify-between gap-4 border-b pb-4 md:flex-row md:items-end"
          >
            <div className="max-w-4xl">
              <div className="mb-4 flex flex-wrap items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">
                <span>Propiedades</span><span aria-hidden="true">/</span>
                <span>En venta</span><span aria-hidden="true">/</span>
                <span className="text-foreground">Obra nueva</span>
              </div>
              <h1 className="text-4xl font-semibold leading-[1.05] text-foreground sm:text-5xl lg:text-6xl">{property.titulo}</h1>
              <p className="mt-4 flex items-center gap-2 text-sm text-muted-foreground sm:text-base">
                <MapPin className="h-4 w-4 shrink-0 text-accent" /> {property.ubicacion}
              </p>
            </div>
            <div className="shrink-0 md:text-right">
              <p className="text-[10px] font-semibold uppercase tracking-[0.2em] text-muted-foreground">Precio de venta</p>
              <p className="mt-1 text-3xl font-semibold text-foreground sm:text-4xl">{formatPrice(property.precio)}</p>
              <p className="mt-1 text-sm text-muted-foreground">{fmtEur(Math.round(property.precio / property.metros_cuadrados))} / m²</p>
            </div>
          </motion.header>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.75, delay: 0.12, ease: "easeOut" }}
            className="grid min-h-[440px] grid-cols-1 gap-1.5 md:h-[520px] md:grid-cols-4"
          >
            <div className="group relative min-h-[390px] min-w-0 overflow-hidden rounded-3xl border-0 bg-card/60 float-shadow md:col-span-3 md:min-h-0">
              <AnimatePresence mode="wait">
                <motion.img
                  key={currentImg}
                  src={optimizedImageUrl(images[currentImg], 1280, 82)}
                  srcSet={`${optimizedImageUrl(images[currentImg], 768, 78)} 768w, ${optimizedImageUrl(images[currentImg], 1280, 82)} 1280w, ${optimizedImageUrl(images[currentImg], 1600, 84)} 1600w`}
                  sizes="(max-width: 1024px) calc(100vw - 32px), 58vw"
                  alt={property.titulo}
                  loading={currentImg === 0 ? "eager" : "lazy"}
                  fetchPriority={currentImg === 0 ? "high" : "auto"}
                  decoding="async"
                  initial={{ opacity: 0, scale: 1.01 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0 }}
                  transition={{ duration: 0.45, ease: "easeOut" }}
                  className="h-full w-full cursor-pointer object-contain transition-transform duration-1000 group-hover:scale-[1.01]"
                  onClick={() => setLightbox(true)}
                />
              </AnimatePresence>


              {images.length > 1 && (
                <>
                  <Button onClick={prevImg} aria-label="Imagen anterior" size="icon" variant="secondary" className="absolute left-4 top-1/2 z-10 -translate-y-1/2 rounded-full border border-card/80 bg-card/90 shadow-lg backdrop-blur-xl transition-transform hover:scale-110">
                    <ChevronLeft className="w-5 h-5" />
                  </Button>
                  <Button onClick={nextImg} aria-label="Imagen siguiente" size="icon" variant="secondary" className="absolute right-4 top-1/2 z-10 -translate-y-1/2 rounded-full border border-card/80 bg-card/90 shadow-lg backdrop-blur-xl transition-transform hover:scale-110">
                    <ChevronRight className="w-5 h-5" />
                  </Button>
                </>
              )}

            </div>

            <div className="grid min-h-[440px] grid-cols-3 gap-1.5 md:min-h-0 md:grid-cols-1 md:grid-rows-3">
              {images.slice(1, 3).map((img, offset) => (
                <Button
                  key={img}
                  type="button"
                  variant="ghost"
                  onClick={() => setCurrentImg(offset + 1)}
                  className="group/side relative h-auto min-h-0 overflow-hidden rounded-2xl border-0 bg-card/60 p-0 float-shadow transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
                  aria-label="Ver imagen"
                >
                  <img src={optimizedImageUrl(img, 560, 76)} alt="" loading="lazy" decoding="async" className="h-full w-full object-contain transition-transform duration-700 group-hover/side:scale-[1.03]" />
                </Button>
              ))}
              <Button
                type="button"
                variant="ghost"
                onClick={() => setShowAll(true)}
                className="group/gallery relative h-auto min-h-0 overflow-hidden rounded-2xl border-0 bg-primary p-0 text-primary-foreground float-shadow-lg transition-all duration-500 hover:-translate-y-1 hover:shadow-2xl"
                aria-label={`Ver las ${images.length} fotos`}
              >
                <img src={optimizedImageUrl(images[3] || images[0], 560, 70)} alt="" loading="lazy" decoding="async" className="absolute inset-0 h-full w-full object-contain opacity-30 transition duration-700 group-hover/gallery:scale-[1.03] group-hover/gallery:opacity-40" />
                <motion.span animate={{ y: [0, -5, 0] }} transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }} className="relative z-10 flex flex-col items-center gap-1">
                  <span className="text-2xl font-semibold">+{Math.max(images.length - 3, 0)}</span>
                  <span className="flex items-center gap-2 text-[10px] font-semibold uppercase tracking-[0.18em]"><Images className="h-4 w-4" /> Ver galería</span>
                </motion.span>
              </Button>
            </div>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-40px" }}
            transition={{ duration: 0.6 }}
            className="glass-surface float-shadow-lg relative z-20 mx-3 -mt-3 grid grid-cols-2 overflow-hidden rounded-2xl sm:grid-cols-3 lg:mx-10 lg:grid-cols-5"
          >
            {specs.slice(0, 5).map((s, i) => (
              <div key={i} className="group min-w-0 border-r border-b p-4 transition-all duration-300 hover:-translate-y-1 hover:bg-card/80 sm:p-5 lg:border-b-0">
                <s.icon className="mb-2 h-4 w-4 text-accent transition-transform duration-300 group-hover:-translate-y-1" />
                <p className="text-[9px] font-bold uppercase tracking-widest text-muted-foreground">{s.label}</p>
                <p className="mt-1 text-base font-bold text-foreground">{s.value}</p>
              </div>
            ))}
          </motion.div>
          <p className="mt-3 flex items-start gap-2 text-[11px] italic leading-relaxed text-muted-foreground">
            <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
            Todas las imágenes son renderizadas y constituyen únicamente una idea del diseño que la casa podría tener.
          </p>
        </section>

        {/* Main content */}
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-5 md:py-7">
          <div className="grid grid-cols-1 gap-10 lg:grid-cols-3 lg:gap-12">
            <div className="lg:col-span-2 space-y-8">
              {/* Description */}
              <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.7 }}>
                <p className="text-[10px] tracking-[0.3em] uppercase text-foreground font-semibold mb-2">Descripción</p>
                <div className="h-px divider-gold mb-4 max-w-[120px]" />
                <h2 className="mb-5 max-w-3xl text-2xl font-semibold leading-snug text-foreground md:text-3xl">
                  Arquitectura contemporánea, amplitud y una conexión natural con el exterior.
                </h2>
                <p className="max-w-3xl text-base font-normal leading-loose text-muted-foreground md:text-lg first-letter:float-left first-letter:mr-2 first-letter:text-5xl first-letter:font-semibold first-letter:leading-none first-letter:text-foreground">
                  <EmphasizedDescription text={property.descripcion} />
                </p>
                <div className="mt-5 grid grid-cols-1 gap-px overflow-hidden rounded-24 border bg-border float-shadow sm:grid-cols-3">
                  <div className="bg-card p-5"><span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Proyecto</span><strong className="mt-2 block text-lg">Obra nueva 2026</strong></div>
                  <div className="bg-card p-5"><span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Inicio previsto</span><strong className="mt-2 block text-lg">Septiembre de 2026</strong></div>
                  <div className="bg-card p-5"><span className="text-[10px] font-semibold uppercase tracking-widest text-muted-foreground">Finalización</span><strong className="mt-2 block text-lg">Otoño de 2027</strong></div>
                </div>
              </motion.div>

              {/* Info general */}
              {infoItems.length > 0 && (
                <motion.div initial={{ opacity: 0, y: 22 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, margin: "-80px" }} transition={{ duration: 0.65 }}>
                  <p className="text-[10px] tracking-[0.3em] uppercase text-foreground font-semibold mb-3">Información general</p>
                  <div className="h-px divider-gold mb-8 max-w-[120px]" />
                  <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10">
                    {infoItems.map((item, i) => (
                      <div
                        key={i}
                        className="group flex items-center justify-between gap-4 border-b py-4 transition-transform duration-300 hover:translate-x-1"
                      >
                        <dt className="flex items-center gap-3 text-muted-foreground">
                          <item.icon className="w-4 h-4 text-amber-600 shrink-0" />
                          <span className="text-[11px] tracking-[0.18em] uppercase">{item.label}</span>
                        </dt>
                        <dd className="text-right text-sm font-bold text-foreground md:text-base">
                          {item.value}
                        </dd>
                      </div>
                    ))}
                  </dl>
                </motion.div>
              )}

              {/* Lo que tienes cerca */}
              <div>
                <motion.div
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true, margin: "-40px" }}
                  transition={{ duration: 0.6, ease: "easeOut" }}
                >
                  <div className="mb-5 flex items-end justify-between gap-4">
                    <div>
                      <p className="text-[10px] tracking-[0.3em] uppercase text-foreground font-semibold mb-2">Entorno</p>
                      <div className="h-px divider-gold max-w-[120px]" />
                      <h2 className="mt-4 text-2xl font-semibold text-foreground">Lo que tienes cerca</h2>
                    </div>
                    <p className="hidden text-xs text-muted-foreground sm:block">Distancias estimadas desde la propiedad</p>
                  </div>

                  <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                    {[
                      { title: "Movilidad", items: lifestyle.transport },
                      { title: "Día a día", items: lifestyle.daily },
                      { title: "Familia y ocio", items: lifestyle.family },
                    ].map((col, colIndex) => (
                      <motion.div
                        key={col.title}
                        initial={{ opacity: 0, y: 16 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.45, delay: colIndex * 0.08, ease: "easeOut" }}
                        className="group rounded-2xl border border-border/60 bg-card/60 p-5 float-shadow transition-all duration-300 hover:-translate-y-1 hover:border-accent/30 hover:bg-card"
                      >
                        <div className="mb-4 flex items-center gap-2.5">
                          <span className="flex h-8 w-8 items-center justify-center rounded-full bg-accent/10 text-accent transition-transform duration-300 group-hover:scale-110">
                            {col.title === "Movilidad" && <Train className="h-4 w-4" />}
                            {col.title === "Día a día" && <Coffee className="h-4 w-4" />}
                            {col.title === "Familia y ocio" && <Trees className="h-4 w-4" />}
                          </span>
                          <p className="text-[10px] tracking-[0.2em] uppercase text-accent font-semibold">{col.title}</p>
                        </div>
                        <ul className="space-y-3.5">
                          {col.items.map((it) => (
                            <li
                              key={it.name}
                              className="flex items-start gap-3 rounded-xl p-2.5 -mx-2 transition-colors duration-200 hover:bg-background/60"
                            >
                              <it.icon className="mt-0.5 h-4 w-4 shrink-0 text-foreground/70" />
                              <div className="flex-1 min-w-0">
                                <p className="text-sm font-medium text-foreground leading-tight">{it.name}</p>
                                <p className="mt-1 text-[11px] text-muted-foreground">
                                  <span className="tabular-nums font-semibold text-foreground/80">{it.time}</span> en {it.mode}
                                </p>
                              </div>
                            </li>
                          ))}
                        </ul>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-4 flex items-start gap-2 rounded-xl border border-border/40 bg-background/50 p-3 text-[11px] text-muted-foreground">
                    <Info className="mt-0.5 h-3.5 w-3.5 shrink-0 text-accent" />
                    <p>Tiempos aproximados en condiciones normales de tráfico. Pueden variar según la hora del día.</p>
                  </div>
                </motion.div>
              </div>


              {/* Map */}
              <div>
                <p className="text-[10px] tracking-[0.3em] uppercase text-foreground font-semibold mb-3">Ubicación</p>
                <div className="h-px divider-gold mb-6 max-w-[120px]" />
                <div className="rounded-3xl overflow-hidden border border-gold/30 ring-gold-soft float-shadow">
                  <PropertyMap
                    locations={[{ address: property.ubicacion, title: property.titulo, price: formatPrice(property.precio) }]}
                    single
                  />
                </div>
              </div>

              {property.carpeta_drive && (
                <div className="bg-foreground border border-gold/40 rounded-3xl p-6 flex items-center justify-between gap-4 ring-gold-soft float-shadow-lg">
                  <div>
                    <p className="text-gradient-gold font-light text-lg">Galería completa</p>
                    <p className="text-background/60 text-sm font-light mt-0.5">Accede a todas las fotografías en alta resolución</p>
                  </div>
                  <a
                    href={property.carpeta_drive}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-5 py-2.5 rounded-full bg-gold-gradient text-black font-semibold text-xs tracking-wider uppercase hover:opacity-90 transition shrink-0"
                  >
                    <ExternalLink className="w-3.5 h-3.5" />
                    Ver fotos
                  </a>
                </div>
              )}
            </div>

            <div id="contacto" className="lg:col-span-1 scroll-mt-28">
              <div className="sticky top-24">
                <ContactSection propertyTitle={property.titulo} propertyId={property.id} />
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Lightbox */}
      <AnimatePresence>
        {lightbox && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[100] flex items-center justify-center bg-black pb-28 pt-14"
            style={{ backgroundColor: "#000" }}
            onClick={() => setLightbox(false)}
          >
            <button aria-label="Cerrar imagen ampliada" className="absolute top-5 right-5 text-white/80 hover:text-amber-300 z-10 transition">
              <X className="w-8 h-8" />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); prevImg(); }}
              aria-label="Imagen anterior"
              className="absolute left-5 top-1/2 -translate-y-1/2 text-white/80 hover:text-amber-300 transition"
            >
              <ChevronLeft className="w-10 h-10" />
            </button>
            <img
              src={optimizedImageUrl(images[currentImg], 1920, 88)}
              alt=""
              decoding="async"
              className="max-h-[calc(100vh-8rem)] max-w-[92vw] rounded-2xl object-contain shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            />
            <button
              onClick={(e) => { e.stopPropagation(); nextImg(); }}
              aria-label="Imagen siguiente"
              className="absolute right-5 top-1/2 -translate-y-1/2 text-white/80 hover:text-amber-300 transition"
            >
              <ChevronRight className="w-10 h-10" />
            </button>

            <div
              className="absolute inset-x-0 bottom-0 flex gap-2 overflow-x-auto bg-black/80 px-5 py-4"
              onClick={(e) => e.stopPropagation()}
            >
              {images.map((img, i) => (
                <button
                  key={`lb-${img}-${i}`}
                  type="button"
                  onClick={() => setCurrentImg(i)}
                  aria-label="Ver imagen"
                  className={`relative h-14 w-20 shrink-0 overflow-hidden rounded-md border transition ${i === currentImg ? "border-accent opacity-100" : "border-white/20 opacity-60 hover:opacity-100"}`}
                >
                  <img src={optimizedImageUrl(img, 160, 60)} alt="" loading="lazy" decoding="async" className="h-full w-full object-contain" />
                </button>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Todas las fotos */}
      <AnimatePresence>
        {showAll && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-[99] overflow-y-auto bg-background"
          >
            <div className="glass-surface sticky top-0 z-10 px-4 py-4 sm:px-8">
              <div className="mx-auto flex max-w-6xl flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                <div>
                  <p className="text-sm font-semibold text-foreground">
                    Fotos por ambientes <span className="text-muted-foreground">({images.length})</span>
                  </p>
                  <p className="mt-1 text-xs text-muted-foreground">Clasificadas entre exterior, salón, cocina, habitaciones, baños, distribución y planos.</p>
                </div>
                <Button variant="secondary" size="sm" className="w-fit rounded-full" onClick={() => setShowAll(false)}>
                  <X className="mr-2 h-4 w-4" /> Cerrar
                </Button>
              </div>
              <div className="mx-auto mt-4 flex max-w-6xl gap-2 overflow-x-auto pb-1">
                <Button
                  type="button"
                  size="sm"
                  variant={selectedAmbit === "todo" ? "default" : "outline"}
                  className="shrink-0 rounded-full"
                  onClick={() => setSelectedAmbit("todo")}
                >
                  Todo ({images.length})
                </Button>
                {photoAmbits.map((ambit) => (
                  <Button
                    key={ambit.id}
                    type="button"
                    size="sm"
                    variant={selectedAmbit === ambit.id ? "default" : "outline"}
                    className="shrink-0 rounded-full"
                    onClick={() => setSelectedAmbit(ambit.id)}
                    title={ambit.description}
                  >
                    {ambit.label} ({ambit.indexes.length})
                  </Button>
                ))}
              </div>
            </div>
            <div className="mx-auto max-w-6xl p-3 sm:p-4 space-y-8">
              {(selectedAmbit === "todo"
                ? photoAmbits.map((ambit) => ({
                    ambit,
                    photos: selectedPhotos.filter((p) => p.ambit?.id === ambit.id),
                  })).filter((g) => g.photos.length > 0)
                : [
                    {
                      ambit: photoAmbits.find((a) => a.id === selectedAmbit) ?? {
                        id: selectedAmbit,
                        label: selectedAmbit,
                        description: "",
                        indexes: selectedIndexes,
                      },
                      photos: selectedPhotos,
                    },
                  ]
              ).map(({ ambit, photos }) => (
                <section key={ambit.id} className="space-y-3">
                  <div className="flex items-center gap-3">
                    <h3 className="text-sm font-semibold text-foreground">{ambit.label}</h3>
                    <span className="text-xs text-muted-foreground">({photos.length})</span>
                    <div className="h-px flex-1 bg-border" />
                  </div>
                  <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 lg:grid-cols-4">
                    {photos.map(({ src, index, ambit: pAmbit }) => (
                      <button
                        key={`grid-${src}-${index}`}
                        type="button"
                        onClick={() => { setCurrentImg(index); setShowAll(false); setLightbox(true); }}
                        aria-label={`Ampliar imagen de ${pAmbit?.label || "galería"}`}
                        className="group relative block overflow-hidden rounded-2xl border-0 bg-card text-left float-shadow transition-all hover:-translate-y-1 hover:shadow-2xl"
                      >
                        <img
                          src={optimizedImageUrl(src, 640, 74)}
                          alt={`${property.titulo} — ${pAmbit?.label || "foto"}`}
                          loading="lazy"
                          decoding="async"
                          className="w-full h-auto object-contain transition-transform duration-700 group-hover:scale-[1.02]"
                        />
                        <span className="absolute left-2 top-2 z-10 rounded-full bg-background/90 px-2 py-1 text-[9px] font-semibold uppercase tracking-wider text-foreground shadow-sm backdrop-blur-sm">
                          {pAmbit?.label || "Galería"}
                        </span>
                      </button>
                    ))}
                  </div>
                </section>
              ))}
            </div>
          </motion.div>
        )}
      </AnimatePresence>

    </Layout>
  );
}
